import { useState } from 'react';
import { X, Palette } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { API_BASE } from '../apiConfig';

const API = API_BASE;

const COLORS = [
  "#0d9488", "#0891b2", "#2563eb", "#7c3aed", "#c026d3",
  "#db2777", "#e11d48", "#ea580c", "#d97706", "#65a30d",
  "#059669", "#0284c7", "#4f46e5", "#9333ea", "#c026d3",
  "#e11d48", "#f97316", "#84cc16", "#14b8a6", "#6366f1",
];

// Lighter tint per base color for gradient top
function lighten(hex, amt = 40) {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.min(255, (n >> 16) + amt);
  const g = Math.min(255, ((n >> 8) & 0xff) + amt);
  const b = Math.min(255, (n & 0xff) + amt);
  return `#${(1 << 24 | r << 16 | g << 8 | b).toString(16).slice(1)}`;
}

// ─── Crisp, bold SVG icon paths ───
const ICON_PATHS = {
  // Animals
  bear: "M12 3c-1.66 0-3 1.34-3 3 0 .55.15 1.06.41 1.5H5a3 3 0 000 6h.1C6.03 15.87 8.7 18 12 18s5.97-2.13 6.9-4.5H19a3 3 0 000-6h-4.41c.26-.44.41-.95.41-1.5 0-1.66-1.34-3-3-3zM9.5 12a1.25 1.25 0 110-2.5 1.25 1.25 0 010 2.5zm5 0a1.25 1.25 0 110-2.5 1.25 1.25 0 010 2.5zM12 15c-1.38 0-2.5-.56-2.5-1.25h5c0 .69-1.12 1.25-2.5 1.25z",
  cat: "M4.5 2L7 7h10l2.5-5h-1L16 5.5 14 2h-4L8 5.5 5.5 2h-1zM6 8c-1.1 0-2 .9-2 2v6c0 2.76 3.58 5 8 5s8-2.24 8-5v-6c0-1.1-.9-2-2-2H6zm3 5a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm6 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm-3 3.5c-1.93 0-3.5-.67-3.5-1.5h7c0 .83-1.57 1.5-3.5 1.5z",
  dog: "M18 4h-2l-2 3H10L8 4H6C3.79 4 2 5.79 2 8v3a3 3 0 003 3h.17C6.06 16.36 8.79 18 12 18s5.94-1.64 6.83-4H19a3 3 0 003-3V8c0-2.21-1.79-4-4-4zM9 11.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm6 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3zM12 16c-2 0-3.5-.9-3.5-2h7c0 1.1-1.5 2-3.5 2z",
  rabbit: "M9 2a2 2 0 00-2 2v4H5a2 2 0 00-2 2v4c0 3.31 4.03 6 9 6s9-2.69 9-6v-4a2 2 0 00-2-2h-2V4a2 2 0 00-2-2h-1v5h-2V2H9zm.5 11a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm5 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3z",
  bird: "M20 4L17 8l-2-1-3 2-3-2-2 1L4 4v2l4.5 5v3c0 2.76 1.57 5 3.5 5s3.5-2.24 3.5-5v-3L20 6V4zM9.5 12.5a1 1 0 110-2 1 1 0 010 2zm5 0a1 1 0 110-2 1 1 0 010 2z",
  fish: "M12 4C7 4 3 7.58 2 12c1 4.42 5 8 10 8s9-3.58 10-8c-1-4.42-5-8-10-8zm0 13a5 5 0 110-10 5 5 0 010 10zm0-8a3 3 0 100 6 3 3 0 000-6z",
  turtle: "M12 4C8.13 4 5 7.13 5 11v1H3.5a1.5 1.5 0 100 3H5v1c0 2.76 3.13 5 7 5s7-2.24 7-5v-1h1.5a1.5 1.5 0 100-3H19v-1c0-3.87-3.13-7-7-7zm-2 9a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm4 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3z",
  owl: "M12 2C7.03 2 3 6.03 3 11c0 3.59 2.1 6.69 5.13 8.13L7 22l5-2.5L17 22l-1.13-2.87C18.9 17.69 21 14.59 21 11c0-4.97-4.03-9-9-9zM8.5 13a2.5 2.5 0 110-5 2.5 2.5 0 010 5zm7 0a2.5 2.5 0 110-5 2.5 2.5 0 010 5z",
  fox: "M12 2L7 7 2 9l3.5 5L2 19h5.5L12 22l4.5-3H22l-3.5-5L22 9l-5-2L12 2zm-2.5 11a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm5 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3z",
  penguin: "M12 2C9 2 6.5 5.13 6.5 9v3.5c0 1.1.9 2 2 2H9v5a3 3 0 006 0v-5h.5c1.1 0 2-.9 2-2V9c0-3.87-2.5-7-5.5-7zM9.5 11a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm5 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3z",
  butterfly: "M12 2L8 7 3 9l2.5 3L3 15l5 2 4 5 4-5 5-2-2.5-3L21 9l-5-2L12 2zm0 6a4 4 0 100 8 4 4 0 000-8z",
  bee: "M12 2C9.24 2 7 4.69 7 8c0 .64.08 1.26.24 1.84L5 11a2.5 2.5 0 000 5l2.24 1.16C8.44 19.12 10.04 21 12 21s3.56-1.88 4.76-3.84L19 16a2.5 2.5 0 000-5l-2.24-1.16C16.92 9.26 17 8.64 17 8c0-3.31-2.24-6-5-6zM10 10h4v1.5h-4V10zm0 3h4v1.5h-4V13z",
  // Shapes
  star: "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z",
  heart: "M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z",
  diamond: "M12 2L2 12l10 10 10-10L12 2zm0 4l6 6-6 6-6-6 6-6z",
  moon: "M12.1 22c-4.42 0-8-3.58-8-8 0-3.75 2.56-6.94 6.1-7.82-.55 1.18-.85 2.5-.85 3.87 0 4.97 4.03 9 9 9 .73 0 1.44-.09 2.12-.25C18.96 21.05 15.74 22 12.1 22z",
  sun: "M12 7a5 5 0 100 10 5 5 0 000-10zM12 2v2m0 16v2M4.22 4.22l1.42 1.42m12.73 12.73l1.42 1.42M2 12h2m16 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42",
  cloud: "M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z",
  circle: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 16a6 6 0 110-12 6 6 0 010 12z",
  triangle: "M12 3L2 21h20L12 3zm0 5l5.5 11h-11L12 8z",
  hexagon: "M12 2l8.66 5v10L12 22l-8.66-5V7L12 2zm0 4L7.34 9v6L12 18l4.66-3V9L12 6z",
  // Symbols — bold, fun, recognisable
  lightning: "M13 2L4 14h7l-2 8 9-12h-7l2-8z",
  rocket: "M12 2c-2.4 2.4-4 5.5-4 8.5 0 1.5.4 3 1.2 4.2L8 16l-.5 3.5L11 18l1 4 1-4 3.5 1.5L16 16l-1.2-1.3c.8-1.2 1.2-2.7 1.2-4.2 0-3-1.6-6.1-4-8.5zm0 5a2 2 0 110 4 2 2 0 010-4z",
  crown: "M2 19h20v3H2v-3zM4 8l4 4 4-6 4 6 4-4-2 10H6L4 8z",
  music: "M12 3v10.55A4 4 0 1014 17V7h4V3h-6zm-4 16a2 2 0 100-4 2 2 0 000 4z",
  puzzle: "M20.5 11H19V7c0-1.1-.9-2-2-2h-4V3.5a2.5 2.5 0 00-5 0V5H4c-1.1 0-2 .9-2 2v3.8h1.5a2.7 2.7 0 010 5.4H2V20c0 1.1.9 2 2 2h3.8v-1.5a2.7 2.7 0 015.4 0V22H17c1.1 0 2-.9 2-2v-4h1.5a2.5 2.5 0 000-5z",
  globe: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z",
  shield: "M12 2L4 5v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V5l-8-3zm0 4l4 2v4c0 2.97-1.63 5.65-4 6.93-2.37-1.28-4-3.96-4-6.93V8l4-2z",
  atom: "M12 11a1 1 0 100 2 1 1 0 000-2zm-7.48 2c-.56 0-1.02.46-1.02 1.02 0 .57.46 1.03 1.02 1.03.57 0 1.03-.46 1.03-1.03 0-.56-.46-1.02-1.03-1.02zm14.96 0c-.56 0-1.02.46-1.02 1.02 0 .57.46 1.03 1.02 1.03.57 0 1.03-.46 1.03-1.03 0-.56-.46-1.02-1.03-1.02zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z",
  flame: "M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67zM11.71 19c-1.78 0-3.22-1.4-3.22-3.14 0-1.62 1.05-2.76 2.81-3.12 1.77-.36 3.6-1.21 4.62-2.58.39 1.29.59 2.65.59 4.04 0 2.65-2.15 4.8-4.8 4.8z",
  anchor: "M12 2a3 3 0 00-1 5.83V11H6l1.29-1.29a1 1 0 00-1.42-1.42l-3 3a1 1 0 000 1.42l3 3a1 1 0 001.42-1.42L6 13h5v7.17A5.002 5.002 0 017 15H5a7 7 0 0014 0h-2a5.002 5.002 0 01-4 4.9V13h5l-1.29 1.29a1 1 0 001.42 1.42l3-3a1 1 0 000-1.42l-3-3a1 1 0 00-1.42 1.42L18 11h-5V7.83A3 3 0 0012 2z",
  compass: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-5.5-2.5l7.51-3.49L17.5 6.5 9.99 9.99 6.5 17.5zm5.5-6.6c.61 0 1.1.49 1.1 1.1s-.49 1.1-1.1 1.1-1.1-.49-1.1-1.1.49-1.1 1.1-1.1z",
  key: "M12.65 10A5.99 5.99 0 007 6c-3.31 0-6 2.69-6 6s2.69 6 6 6a5.99 5.99 0 005.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z",
  // Nature
  leaf: "M17.12 5.88C14.46 3.22 10.12 2.56 6 4c-.25.09-.47.24-.65.42L4 5.77C2.75 7.02 2 8.97 2 11c0 4 2 7 6 9l2.35-2.35c1.76.6 3.65.35 5.17-.88l.36-.36c1.76-1.76 1.76-4.6 0-6.36L17.12 5.88z",
  flower: "M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm0-18a4 4 0 00-4 4c0 .34.04.67.1 1A4 4 0 005 13a4 4 0 003.38 3.95A4 4 0 0012 20a4 4 0 003.62-3.05A4 4 0 0019 13a4 4 0 00-3.1-3.9c.06-.36.1-.72.1-1.1a4 4 0 00-4-4z",
  tree: "M12 2L5 11h3L5 16h3l-3 6h14l-3-6h3l-3-5h3L12 2z",
};

function defaultColor(name) {
  const h = (name || '?').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return COLORS[h % COLORS.length];
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Unique gradient id per avatar instance
let gradIdCounter = 0;

// ═══ MAIN AVATAR COMPONENT ═══
export function StudentAvatar({ student, size = 'md', onClick, className = '' }) {
  const name = student?.name || '?';
  const color = student?.avatar_color || defaultColor(name);
  const icon = student?.avatar_icon || 'initials';
  const initials = getInitials(name);
  const gradId = `av-g-${student?.student_id || (++gradIdCounter)}`;

  const sizes = {
    xs: 'w-6 h-6 text-[9px]',
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-14 h-14 text-lg',
    xl: 'w-20 h-20 text-2xl',
  };
  const iconSizes = { xs: 12, sm: 14, md: 18, lg: 26, xl: 38 };
  const ringWidth = { xs: 2, sm: 2, md: 2, lg: 3, xl: 4 };

  const lightColor = lighten(color, 50);

  return (
    <div
      onClick={onClick}
      className={`${sizes[size]} rounded-full flex items-center justify-center font-bold text-white flex-shrink-0 select-none relative ${onClick ? 'cursor-pointer hover:scale-110 transition-transform active:scale-95' : 'transition-transform'} ${className}`}
      style={{
        background: `linear-gradient(135deg, ${lightColor} 0%, ${color} 100%)`,
        boxShadow: `0 2px 8px ${color}55, inset 0 1px 0 ${lightColor}44`,
        border: `${ringWidth[size]}px solid rgba(255,255,255,0.25)`,
      }}
      data-testid={`avatar-${student?.student_id || 'unknown'}`}
      title={name}
    >
      {icon === 'initials' || !ICON_PATHS[icon] ? (
        <span style={{ textShadow: '0 1px 2px rgba(0,0,0,0.25)', letterSpacing: '0.5px' }}>{initials}</span>
      ) : (
        <svg width={iconSizes[size]} height={iconSizes[size]} viewBox="0 0 24 24" style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.2))' }}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fff" stopOpacity="1" />
              <stop offset="100%" stopColor="#fff" stopOpacity="0.7" />
            </linearGradient>
          </defs>
          <path d={ICON_PATHS[icon]} fill={`url(#${gradId})`} />
        </svg>
      )}
    </div>
  );
}

// ═══ AVATAR PICKER MODAL ═══
export function AvatarPicker({ student, onClose, onUpdate }) {
  const [color, setColor] = useState(student?.avatar_color || defaultColor(student?.name));
  const [icon, setIcon] = useState(student?.avatar_icon || 'initials');
  const [saving, setSaving] = useState(false);

  const iconCategories = [
    { label: 'Default', items: ['initials'] },
    { label: 'Animals', items: ['bear', 'cat', 'dog', 'rabbit', 'bird', 'fish', 'turtle', 'owl', 'fox', 'penguin', 'butterfly', 'bee'] },
    { label: 'Symbols', items: ['lightning', 'rocket', 'crown', 'music', 'puzzle', 'globe', 'shield', 'atom', 'flame', 'anchor', 'compass', 'key'] },
    { label: 'Shapes', items: ['star', 'heart', 'diamond', 'circle', 'triangle', 'hexagon', 'moon', 'sun', 'cloud'] },
    { label: 'Nature', items: ['leaf', 'flower', 'tree'] },
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/students/${student.student_id}/avatar`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ avatar_color: color, avatar_icon: icon })
      });
      if (r.ok) {
        const updated = await r.json();
        onUpdate(updated);
        toast.success('Avatar updated');
        onClose();
      } else { toast.error('Failed to update avatar'); }
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const preview = { ...student, avatar_color: color, avatar_icon: icon };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="avatar-picker">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <Palette className="w-5 h-5 text-teal-600" />
            <h3 className="font-semibold text-slate-900">Choose Avatar</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
          <div className="flex flex-col items-center gap-2">
            <StudentAvatar student={preview} size="xl" />
            <p className="text-sm font-medium text-slate-700">{student.name}</p>
          </div>

          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Colour</p>
            <div className="grid grid-cols-10 gap-1.5">
              {COLORS.map((c, i) => (
                <button key={`${c}-${i}`} onClick={() => setColor(c)}
                  className={`w-7 h-7 rounded-full transition-all shadow-sm ${color === c ? 'ring-2 ring-offset-2 ring-slate-800 scale-110' : 'hover:scale-110'}`}
                  style={{ background: `linear-gradient(135deg, ${lighten(c, 50)} 0%, ${c} 100%)` }}
                  data-testid={`color-${i}`} />
              ))}
            </div>
          </div>

          {iconCategories.map(cat => (
            <div key={cat.label}>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{cat.label}</p>
              <div className="grid grid-cols-6 gap-2">
                {cat.items.map(ic => (
                  <button key={ic} onClick={() => setIcon(ic)}
                    className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all shadow-sm ${
                      icon === ic ? 'bg-slate-900 text-white ring-2 ring-offset-1 ring-slate-800 scale-105' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:scale-105'
                    }`}
                    data-testid={`icon-${ic}`}>
                    {ic === 'initials' ? (
                      <span className="text-xs font-bold">{getInitials(student.name)}</span>
                    ) : ICON_PATHS[ic] ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d={ICON_PATHS[ic]} />
                      </svg>
                    ) : (
                      <span className="text-xs">{ic}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-slate-100 flex gap-2">
          <Button variant="outline" className="flex-1 rounded-xl" onClick={onClose}>Cancel</Button>
          <Button className="flex-1 rounded-xl bg-teal-600 hover:bg-teal-700 text-white" onClick={handleSave} disabled={saving}
            data-testid="save-avatar-btn">
            {saving ? 'Saving...' : 'Save Avatar'}
          </Button>
        </div>
      </div>
    </div>
  );
}
